import type { Popup, PopupOptions } from '../types'
import { spawnPopup } from './spawnPopup'

/**
 * Create a new empty popup and return a minimal set of API.
 */
export function createEmptyPopup(options: PopupOptions): Popup {
  const { id, windowConfig, plugins = [] } = options

  // window.open returns `Window | null`
  let popupWindow: Window | null = null

  // --------------------------------------------------
  // POPUP LOAD
  // --------------------------------------------------
  const onPopupWindowLoad = () => {
    // Something strange happened
    if (!popupWindow) {
      for (const plugin of plugins) plugin.onError?.({ errorType: 'unknown', id })
      return
    }

    if (loadTimeoutId !== undefined) clearTimeout(loadTimeoutId)

    // onLoad hook
    for (const plugin of plugins) plugin.onLoad?.({ popupWindow, id })
  }

  // --------------------------------------------------
  // POPUP API
  // --------------------------------------------------
  const close = () => {
    if (!popupWindow) {
      for (const plugin of plugins) plugin.onError?.({ errorType: 'unexistingPopup', id })
      return false
    }

    removeEventListeners()

    // onBeforeClose hook
    for (const plugin of plugins)
      plugin.onBeforeClose?.({ initiator: 'onCloseViaApi', popupWindow, id })

    // cleanup
    popupWindow.close()
    popupWindow = null

    // onClose hook
    for (const plugin of plugins) plugin.onClose?.({ initiator: 'onCloseViaApi', id })

    return true
  }

  // --------------------------------------------------
  // POPUP CLOSE
  // --------------------------------------------------
  const onPopupWindowClose = () => {
    if (!popupWindow) {
      for (const plugin of plugins) plugin.onError?.({ errorType: 'unexistingPopup', id })
      return
    }

    removeEventListeners()

    // onBeforeClose hook
    for (const plugin of plugins)
      plugin.onBeforeClose?.({ initiator: 'onPopupWindowClose', popupWindow, id })

    // cleanup
    popupWindow = null

    // onClose hook
    for (const plugin of plugins) plugin.onClose?.({ initiator: 'onPopupWindowClose', id })
  }

  // --------------------------------------------------
  // MAIN WINDOW CLOSE
  // --------------------------------------------------
  const onMainWindowClose = () => {
    if (!popupWindow) {
      for (const plugin of plugins) plugin.onError?.({ errorType: 'unexistingPopup', id })
      return
    }

    removeEventListeners()

    // onBeforeClose hook
    for (const plugin of plugins)
      plugin.onBeforeClose?.({ initiator: 'onMainWindowClose', popupWindow, id })

    // cleanup
    popupWindow.close()
    popupWindow = null

    // onClose hook
    for (const plugin of plugins) plugin.onClose?.({ initiator: 'onMainWindowClose', id })
  }

  // --------------------------------------------------
  // EVENT LISTENERS
  // --------------------------------------------------
  let loadTimeoutId: ReturnType<typeof setTimeout> | undefined
  const addEventListeners = () => {
    if (!popupWindow) return

    // Close management
    window.addEventListener('pagehide', onMainWindowClose)
    popupWindow.addEventListener('pagehide', onPopupWindowClose)

    // Load management
    popupWindow.addEventListener('load', onPopupWindowLoad)

    // Both Firefox and Safari does not trigger the 'load' event for the popups, let's trigger it manually
    loadTimeoutId = setTimeout(onPopupWindowLoad, 1000)
  }

  const removeEventListeners = () => {
    if (!popupWindow) return

    // Close management
    window.removeEventListener('pagehide', onMainWindowClose)
    popupWindow.removeEventListener('pagehide', onPopupWindowClose)

    // Load management
    popupWindow.removeEventListener('load', onPopupWindowLoad)
    if (loadTimeoutId !== undefined) clearTimeout(loadTimeoutId)
  }

  // --------------------------------------------------
  // OPEN API
  // --------------------------------------------------

  const open = () => {
    const config = !!windowConfig
      ? typeof windowConfig === 'function'
        ? windowConfig(id)
        : windowConfig
      : undefined

    // Open the popup
    popupWindow = spawnPopup(id, config?.windowFeatures)

    // The popup did not open, probably because of a popup blocker
    if (!popupWindow) {
      for (const plugin of plugins) plugin.onError?.({ errorType: 'popupBlocked', id })

      return false
    }

    // Set the popup's title
    popupWindow.document.title = config?.title ?? 'Popup'

    // onOpen hook
    for (const plugin of plugins) plugin.onOpen?.({ popupWindow, id })

    // Add all the necessary listeners to the new window
    addEventListeners()

    return true
  }

  // --------------------------------------------------
  // FOCUS API
  // --------------------------------------------------
  const focus = () => {
    if (!popupWindow) return false

    popupWindow.focus()

    return true
  }

  return {
    open,
    close,
    focus,
  }
}
